import BigNumber from "bignumber.js";
import { useEffect, useMemo, useState } from "react";
import { useAlerting, useTranslation } from "src/hooks";
import { displayAmount } from "src/utils/helper";

import MpTextFieldNumberOnly from "../../../../../components/MpTextFieldNumberOnly";
import { Box } from "../../../../../components/MuiGenerals";

interface PricingRow {
  operation: string;
  energyPerUnit: number;
  bandwidthPerUnit: number;
}

interface IProps {
  setFields: any;
}

const RentPricingTable = (props: IProps) => {
  const { t } = useTranslation("tronWalletResourceBalance");
  const { alerting } = useAlerting();

  const PRICING_ROWS: PricingRow[] = [
    // Withdraw
    {
      operation: t("withdrawNoUSDT"),
      energyPerUnit: 130000,
      bandwidthPerUnit: 350,
    },
    {
      operation: t("withdrawHaveUSDT"),
      energyPerUnit: 65000,
      bandwidthPerUnit: 350,
    },
    // Swap exact pay
    {
      operation: t("exactPayFirstTime"),
      energyPerUnit: 103500,
      bandwidthPerUnit: 1350,
    },
    {
      operation: t("exactPaySwappedBefore"),
      energyPerUnit: 3500,
      bandwidthPerUnit: 1000,
    },
    // Swap exact get
    {
      operation: t("exactGetFirstTime"),
      energyPerUnit: 450000,
      bandwidthPerUnit: 1000,
    },
    {
      operation: t("exactGetSwappedBefore"),
      energyPerUnit: 350000,
      bandwidthPerUnit: 600,
    },
  ];
  const { setFields } = props;

  // Store count values for each row
  const [counts, setCounts] = useState<{ [key: number]: string }>({
    0: "",
    1: "",
    2: "",
    3: "",
    4: "",
    5: "",
  });

  const handleCountChange = (index: number, value: string) => {
    const MAX_COUNT = 20;

    if (Number(value) > MAX_COUNT) {
      return alerting("warning", t("exceedMaximumCountMessage"));
    }

    setCounts((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  // Helper to get appropriate font size based on number length
  const getNumberStyle = (value: number) => {
    const valueStr = value.toLocaleString();
    const length = valueStr.length;

    if (length > 20) {
      return { fontSize: "11px" };
    } else if (length > 15) {
      return { fontSize: "12px" };
    }
    return { fontSize: "14px" };
  };

  const getEnergyPerUnitWithMin = (energyPerUnit: number, count: BigNumber) => {
    const MIN_ENERGY = 65000;

    if (count.isZero()) {
      return new BigNumber(0);
    }

    return BigNumber.max(MIN_ENERGY, energyPerUnit).plus(
      count.minus(1).multipliedBy(energyPerUnit)
    );
  };

  const getBandwidthPerUnitWithMin = (
    bandwidthPerUnit: number,
    count: BigNumber
  ) => {
    const MIN_BANDWIDTH = 400;

    if (count.isZero()) {
      return new BigNumber(0);
    }

    return count.multipliedBy(BigNumber.max(bandwidthPerUnit, MIN_BANDWIDTH));
  };

  const calculateRowValues = (index: number) => {
    const count = counts[index] || "0";
    const row = PRICING_ROWS[index];

    const countBN = new BigNumber(count);
    const energyBN = getEnergyPerUnitWithMin(row.energyPerUnit, countBN);
    const bandwidthBN = getBandwidthPerUnitWithMin(
      row.bandwidthPerUnit,
      countBN
    );

    return {
      energy: energyBN.toNumber(),
      bandwidth: bandwidthBN.toNumber(),
    };
  };

  const totals = useMemo(() => {
    let totalCountBN = new BigNumber(0);
    let totalEnergyBN = new BigNumber(0);
    let totalBandwidthBN = new BigNumber(0);

    PRICING_ROWS.forEach((row, index) => {
      const count = counts[index] || "0";
      const countBN = new BigNumber(count);

      totalCountBN = totalCountBN.plus(countBN);

      totalEnergyBN = totalEnergyBN.plus(
        getEnergyPerUnitWithMin(row.energyPerUnit, countBN)
      );

      totalBandwidthBN = totalBandwidthBN.plus(
        getBandwidthPerUnitWithMin(row.bandwidthPerUnit, countBN)
      );
    });

    return {
      totalCount: totalCountBN.toFixed().toString(),
      totalEnergy: totalEnergyBN.toFixed().toString(),
      totalBandwidth: totalBandwidthBN.toFixed().toString(),
    };
  }, [counts]);

  useEffect(() => {
    setFields((prev: any) => ({
      ...prev,
      numberOfTransactionEnergy: totals.totalEnergy,
    }));
    setFields((prev: any) => ({
      ...prev,
      numberOfTransactionBandwidth: totals.totalBandwidth,
    }));
  }, [totals, setFields]);

  return (
    <Box
      sx={{
        marginBottom: "24px",
      }}
    >
      {/* Header Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "2fr 145px minmax(100px, 1fr) minmax(100px, 1fr)", // 4 columns with minimum width for number columns
          gap: "16px",
          padding: "12px 16px",
          // backgroundColor: "#f5f5f5",
          borderRadius: "4px 4px 0 0",
          // borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ fontWeight: 600, color: "#666" }}>Operation</Box>
        <Box sx={{ fontWeight: 600, color: "#666", textAlign: "center" }}>
          {t("count")}
        </Box>
        <Box sx={{ fontWeight: 600, color: "#666", textAlign: "center" }}>
          {t("energy")}
        </Box>
        <Box sx={{ fontWeight: 600, color: "#666", textAlign: "center" }}>
          {t("bandwidth")}
        </Box>
      </Box>

      {/* Data Rows */}
      {PRICING_ROWS.map((row, index) => {
        const { energy, bandwidth } = calculateRowValues(index);

        return (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns:
                "2fr 145px minmax(100px, 1fr) minmax(100px, 1fr)",
              gap: "16px",
              padding: "4px 16px",
              // borderBottom:
              //   index < PRICING_ROWS.length - 1 ? "1px solid #e0e0e0" : "none",
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#fafafa",
              },
              ...(index === PRICING_ROWS.length - 1 && {
                borderRadius: "0 0 4px 4px",
              }),
            }}
          >
            {/* Operation column */}
            <Box
              sx={{
                color: "#333",
                fontSize: "14px",
                whiteSpace: "pre-line", // Preserves line breaks from \n
                lineHeight: 1.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              {row.operation}
            </Box>

            {/* Count column - input field */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <MpTextFieldNumberOnly
                fullWidth
                integerOnly
                size="small"
                placeholder={t("countPlaceHolder")}
                value={counts[index]}
                onChange={(e) => handleCountChange(index, e.target.value)}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "14px",
                  },
                  //   "& .MuiInputBase-input": {
                  //     textAlign: "center",
                  //   },
                }}
              />
            </Box>

            {/* Energy column - calculated value */}
            <Box
              sx={{
                color: "#333",
                ...getNumberStyle(energy),
                fontWeight: 500,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0, // Allow flexbox to shrink below content size
                wordBreak: "break-word", // Break long numbers if needed
                overflowWrap: "break-word", // Additional breaking support
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {energy.toLocaleString()}
            </Box>

            {/* Bandwidth column - calculated value */}
            <Box
              sx={{
                color: "#333",
                ...getNumberStyle(bandwidth),
                fontWeight: 500,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0, // Allow flexbox to shrink below content size
                wordBreak: "break-word", // Break long numbers if needed
                overflowWrap: "break-word", // Additional breaking support
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {bandwidth.toLocaleString()}
            </Box>
          </Box>
        );
      })}

      {/* Total Row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "2fr 145px minmax(100px, 1fr) minmax(100px, 1fr)",
          gap: "16px",
          padding: "12px 16px",
          // backgroundColor: "#f9f9f9",
          // borderTop: "2px solid #e0e0e0",
          borderRadius: "0 0 4px 4px",
          fontWeight: 600,
        }}
      >
        <Box sx={{ color: "#333", fontSize: "14px" }}>{t("total")}</Box>
        <Box
          sx={{
            color: "#333",
            fontSize: "14px",
            textAlign: "center",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayAmount(totals.totalCount, 0)}
        </Box>
        <Box
          sx={{
            color: "#333",
            fontSize: "14px",
            textAlign: "center",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayAmount(totals.totalEnergy, 0)}
        </Box>
        <Box
          sx={{
            color: "#333",
            fontSize: "14px",
            textAlign: "center",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {displayAmount(totals.totalBandwidth, 0)}
        </Box>
      </Box>
    </Box>
  );
};

export default RentPricingTable;
